<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";

const logger = useLogger("ReportProvider");
const props = defineProps<{ id: string }>();
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));
const features = inject("render-features", { editor: false });

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Report Provider editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this report provider",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Report Provider",
    icon: "lucide:box-select",
  },
});

const route = useRoute();
const { reportData, fetchReports, reportFileName: activeReportFileName, setReportFileName } = useReportsStore(props.id);

// --- Provided state: data + kind + context + flags ---
type ReportKind = "users" | "user" | "constructs" | "raw";
const report = reactive<{
  data: any[];
  kind: ReportKind;
  context?: any;
  loading: boolean; // true while fetching/building report data
  initialized: boolean; // false before the first load attempt
  debug: boolean;
  fileName: string;
}>({
  data: [],
  kind: "raw",
  loading: false,
  initialized: false,
  debug: false,
  fileName: "report",
});
provide("report", report);

// ---- helpers ----
/**
 * Runs the custom mapper (if provided) and supports both sync & async mappers.
 * We purposefully do NOT assign to report.data until this promise resolves to
 * avoid a flash of raw -> mapped data in child components.
 */
async function runCustomMapper(rawData: any, context?: any) {
  const defaultMapper = "function mapper(data, context){return data}";
  const mapperCode = definitionData.value?.customMapper || defaultMapper;
  try {
    const fn = new Function(
      "data",
      "context",
      `${mapperCode}; return mapper(data, context)`
    );
    const result = fn(rawData, context);
    return await Promise.resolve(result);
  } catch (e) {
    logger.error("Failed to execute custom mapper", e);
    return rawData;
  }
}

function pickDefaultDataKey(hasUserId: boolean): string {
  // Sensible defaults:
  // - No userId -> ranked pages -> users
  // - With userId -> individual pages -> constructs
  const configured = definitionData.value?.dataKey;
  if (configured && configured.length) return configured;
  return hasUserId ? "constructs" : "users";
}

async function load() {
  // mark start of any load cycle
  report.initialized = true;
  report.loading = true;
  try {
    const resolvedFileName = normalizeReportFileName(definitionData.value?.reportFileName);
    if (report.fileName !== resolvedFileName) {
      report.fileName = resolvedFileName;
    }
    if (activeReportFileName.value !== resolvedFileName) {
      setReportFileName(resolvedFileName);
    }
    report.debug = Boolean(definitionData.value?.debug);
    if (features.editor) {
      const sample = definitionData.value?.sampleData;
      if (sample) {
        try {
          const parsed = JSON.parse(sample);
          const hasUserId = !!parsed?.userId;
          const users = Array.isArray(parsed) ? parsed : [parsed].filter(Boolean);
          const user = users[0] ?? null;
          const dataKey = pickDefaultDataKey(hasUserId);

          let rawData: any;
          let kind: ReportKind;

          if (dataKey === "users") {
            rawData = users;
            kind = "users";
          } else if (dataKey === "*") {
            rawData = hasUserId ? user : users;
            kind = hasUserId ? "user" : "users";
          } else {
            rawData = hasUserId ? (user?.[dataKey] ?? []) : users;
            kind = hasUserId ? "constructs" : "users";
          }

          // Optionally filter constructs
          const selected = definitionData.value?.selectedConstructIds || [];
          if (kind === "constructs" && Array.isArray(selected) && selected.length && Array.isArray(rawData)) {
            rawData = rawData.filter((c: any) => selected.includes(String(c.id)));
          }

          const context = {
            user,
            users,
            config: definitionData.value,
            hasUserId,
            dataKey,
            debug: report.debug,
            reportFileName: report.fileName,
            // Add direct access to raw report structures for templating
            rawUsers: users, // Original user report data with constructs, data arrays, etc.
            rawUser: user, // Single user report data
          };
          report.kind = kind;
          report.context = context;
          // Defer data assignment until after mapping completes
          report.data = await runCustomMapper(rawData, context);
          return;
        } catch (e) {
          logger.error("Invalid sample JSON; falling back to static sample", e);
        }
      }

      // Fallback sample
      const rawData = [
        { id: "comm", name: "Communication", score: 0.45 },
        { id: "lead", name: "Leadership", score: 0.35 },
        { id: "prob", name: "Problem Solving", score: 0.75 },
        { id: "team", name: "Teamwork", score: 0.25 },
      ];
      const context = {
        user: null,
        users: [],
        config: definitionData.value,
        hasUserId: false,
        dataKey: "constructs",
        debug: report.debug,
        reportFileName: report.fileName,
      };
      report.kind = "constructs";
      report.context = context;
      //here
      report.data = await runCustomMapper(rawData, context);
      return;
    }

    // ---- Viewer mode: live data from API ----
    const userId = route.query.user as string | undefined;
    await fetchReports(userId, { fileName: resolvedFileName });

    const users = Array.isArray(reportData.value) ? reportData.value : [];
    const user = users[0] ?? null;
    const hasUserId = !!userId;

    const dataKey = pickDefaultDataKey(hasUserId);
    let rawData: any;
    let kind: ReportKind;

    if (dataKey === "users") {
      // Explicit users list (best for Ranked pages without userId)
      rawData = users;
      kind = "users";
    } else if (dataKey === "*") {
      // Pass through the selected entity depending on mode
      rawData = hasUserId ? user : users;
      kind = hasUserId ? "user" : "users";
    } else {
      if (hasUserId) {
        // Single-user mode: give constructs (or other key) for that user
        rawData = user ? (user as any)[dataKey] ?? [] : [];
        kind = Array.isArray(rawData) ? "constructs" : "raw";
      } else {
        // No userId but a non-"users" dataKey was requested.
        // To avoid child components accidentally rendering constructs as rows,
        // default to the users list in this mode.
        rawData = users;
        kind = "users";
      }
    }

    // Only filter when we are actually returning constructs
    const selected = definitionData.value?.selectedConstructIds || [];
    if (kind === "constructs" && Array.isArray(selected) && selected.length && Array.isArray(rawData)) {
      rawData = rawData.filter((c: any) => selected.includes(String(c.id)));
    }

    // Create enriched context with easy access to all report data
    const context = {
      user,
      users,
      config: definitionData.value,
      hasUserId,
      dataKey,
      debug: report.debug,
      reportFileName: report.fileName,
      // Add direct access to raw report structures for templating
      rawUsers: users, // Original user report data with constructs, data arrays, etc.
      rawUser: user, // Single user report data
    };
    report.kind = kind;
    report.context = context;
    // Defer assigning until mapper completes (supports async)
    report.data = await runCustomMapper(rawData, context);
  } catch (e) {
    logger.error("Failed to load report data", e);
  } finally {
    report.loading = false;
  }
}

onMounted(load);
watch(() => route.query.user, load, { flush: "post" });

function normalizeReportFileName(fileName: unknown): string {
  if (typeof fileName === "string" && fileName.trim().length) {
    return fileName.trim();
  }
  return "report";
}
</script>

<template>
  <loading-indicator
    v-if="report.loading || !report.initialized"
    role="status"
    aria-live="polite"
  />
  <slot v-else />
  <div v-if="features.editor" class="hidden">
    <editor-reports-provider :id="props.id" />
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  editor: () => import("#bb/components/editor/reports/provider.vue"),
  componentId: ComponentDefinitionIds.REPORT_PROVIDER,
  id: "reportprovider",
  icon: "lucide:box-select",
  type: "Report Provider",
  description: "Select and provide data for report components within this container.",
  default: {
    columnsSettings: { default: 1 },
    dataKey: "constructs",
    sampleData: "",
    selectedConstructIds: [],
    reportFileName: "report",
    debug: false,
    children: [],
  },
  categories: ["Reports"],
  order: 12004,
};
</script>
